import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, XCircle, ArrowLeft, RefreshCw } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 100; // ~5 minutes

export default function PaymentPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const [status, setStatus] = useState("pending"); // pending | paid | cancelled | timeout | notfound
  const [eventId, setEventId] = useState(null);
  const pollCount = useRef(0);
  const stopped = useRef(false);

  useEffect(() => {
    // Fetch order once to know which event to link back to
    api
      .get(`/orders/${orderId}`)
      .then((res) => setEventId(res.data?.order?.event_id || null))
      .catch((err) => {
        if (err?.response?.status === 404) setStatus("notfound");
      });
  }, [orderId]);

  useEffect(() => {
    stopped.current = false;
    pollCount.current = 0;

    const poll = async () => {
      if (stopped.current) return;
      try {
        const { data } = await api.get(`/orders/${orderId}/payment-status`);
        if (stopped.current) return;
        if (data.status === "paid" || data.status === "refunded") {
          setStatus("paid");
          stopped.current = true;
          navigate(`/billetter/${orderId}`, { replace: true });
          return;
        }
        if (data.status === "cancelled") {
          setStatus("cancelled");
          stopped.current = true;
          return;
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          setStatus("notfound");
          stopped.current = true;
          return;
        }
        // transient errors: keep polling
      }
      pollCount.current += 1;
      if (pollCount.current >= MAX_POLLS) {
        setStatus("timeout");
        stopped.current = true;
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      stopped.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const retryPolling = () => {
    window.location.reload();
  };

  return (
    <PublicLayout>
      <div className="max-w-lg mx-auto px-4 py-24">
        <Card className="rounded-2xl">
          <CardContent className="pt-10 pb-10 text-center space-y-4">
            {status === "pending" || status === "paid" ? (
              <div data-testid="payment-pending-state">
                <Loader2 className="w-10 h-10 mx-auto animate-spin text-primary" />
                <h1 className="text-xl font-heading font-semibold mt-4">{t("Venter på betalingsbekreftelse")}</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("Fullfør betalingen i Vipps. Denne siden oppdateres automatisk når betalingen er bekreftet.")}
                </p>
              </div>
            ) : status === "cancelled" ? (
              <div data-testid="payment-cancelled-state">
                <XCircle className="w-10 h-10 mx-auto text-destructive" />
                <h1 className="text-xl font-heading font-semibold mt-4">{t("Betalingen ble avbrutt")}</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("Bestillingen er kansellert og ingen billetter er utstedt. Du kan prøve igjen fra arrangementssiden.")}
                </p>
                <Button asChild className="mt-6" data-testid="payment-back-to-event-button">
                  <Link to={eventId ? `/arrangement/${eventId}` : "/"}>
                    <ArrowLeft className="w-4 h-4 mr-1.5" />
                    {eventId ? t("Tilbake til arrangementet") : t("Til forsiden")}
                  </Link>
                </Button>
              </div>
            ) : status === "timeout" ? (
              <div data-testid="payment-timeout-state">
                <RefreshCw className="w-10 h-10 mx-auto text-muted-foreground" />
                <h1 className="text-xl font-heading font-semibold mt-4">{t("Vi venter fortsatt på Vipps")}</h1>
                <p className="text-sm text-muted-foreground mt-2">
                  {t("Vi har ikke fått bekreftelse enda. Hvis du har betalt, prøv å oppdatere siden – billettene dine er trygge.")}
                </p>
                <Button onClick={retryPolling} className="mt-6" data-testid="payment-retry-button">
                  <RefreshCw className="w-4 h-4 mr-1.5" />{t("Sjekk på nytt")}
                </Button>
              </div>
            ) : (
              <div data-testid="payment-notfound-state">
                <XCircle className="w-10 h-10 mx-auto text-destructive" />
                <h1 className="text-xl font-heading font-semibold mt-4">{t("Fant ikke bestillingen")}</h1>
                <p className="text-sm text-muted-foreground mt-2">{t("Sjekk at lenken er riktig.")}</p>
                <Button asChild className="mt-6" data-testid="payment-home-button">
                  <Link to="/"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til forsiden")}</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PublicLayout>
  );
}
